import { Auth0Client } from "@auth0/auth0-spa-js";
import { AxiosRequestConfig } from "axios";

/**
 * Centralized authentication utility functions for all API classes
 */

/**
 * Retrieves an authentication token with proper error handling
 * @param auth0Client - The Auth0 client instance
 * @returns Promise<string> - The authentication token
 * @throws Error if token retrieval fails
 */
export async function getAuthToken(auth0Client: Auth0Client): Promise<string> {
  try {
    if (!auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    return await auth0Client.getTokenSilently();
  } catch (error: any) {
    const errorMessage = error?.error || error;
    const loginRequired =
      errorMessage === "login_required" || errorMessage === "Login required";

    if (loginRequired) {
      console.warn("Session expired → logging out");

      // Small delay to ensure notification is shown, then logout with notification parameter
      // The persistent notification will continue showing during logout
      setTimeout(async () => {
        // Add parameter to indicate logout was due to inactivity
        const returnUrl = `${window.location.origin}?logout_reason=inactivity`;
        await auth0Client.logout({ returnTo: returnUrl });
      }, 500); // Short delay just to ensure notification renders
    }

    console.error("Auth token retrieval failed:", error);
    throw error;
  }
}

/**
 * Creates an Axios request config with authentication headers
 * @param auth0Client - The Auth0 client instance
 * @param rest - Additional headers to include
 * @returns Promise<AxiosRequestConfig> - The request configuration with auth headers
 */
export async function getAuthHeader(
  auth0Client: Auth0Client,
  rest?: Record<string, any>,
): Promise<AxiosRequestConfig> {
  const authToken = await getAuthToken(auth0Client);
  return { headers: { Authorization: `Bearer ${authToken}`, ...rest } };
}

/**
 * Creates a basic config object with authentication headers
 * @param auth0Client - The Auth0 client instance
 * @returns Promise<{ headers: { Authorization: string } }> - Basic config with auth headers
 */
export async function getBasicAuthConfig(
  auth0Client: Auth0Client,
): Promise<{ headers: { Authorization: string } }> {
  const authToken = await getAuthToken(auth0Client);
  return { headers: { Authorization: `Bearer ${authToken}` } };
}

/**
 * Creates a config object with authentication headers and additional properties
 * @param auth0Client - The Auth0 client instance
 * @param additionalProps - Additional properties to add to the config.e.g. signal.
 * @returns Promise<Record<string, unknown>> - Config with auth headers and additional properties
 */
export async function getAuthConfigWithProps(
  auth0Client: Auth0Client,
  additionalProps: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const authToken = await getAuthToken(auth0Client);
  return {
    headers: { Authorization: `Bearer ${authToken}` },
    ...additionalProps,
  };
}
